/**
 * SupervisionTrackingService - Automatic tracking of supervisor viewing sessions
 * Tracks when supervisors view RBT streams for compliance and billing
 */

import {
  doc,
  getDoc,
  updateDoc,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import {
  SupervisionViewingLog,
  SupervisionMethod,
  SessionNote,
} from '../models/SessionNote';
import { v4 as uuidv4 } from 'uuid';

class SupervisionTrackingService {
  /**
   * Start a supervision viewing session
   * Called automatically when supervisor opens a session note
   */
  async startViewingSession(
    sessionNoteId: string,
    supervisorId: string,
    supervisorName: string,
    method: SupervisionMethod,
    billingCode?: string
  ): Promise<string> {
    try {
      const viewingLogId = uuidv4();
      const sessionNoteRef = doc(db, 'sessionNotes', sessionNoteId);
      const sessionNoteDoc = await getDoc(sessionNoteRef);

      if (!sessionNoteDoc.exists()) {
        throw new Error('Session note not found');
      }

      const sessionNote = sessionNoteDoc.data() as SessionNote;

      // Check if there's already an active viewing session for this supervisor
      const activeLog = sessionNote.supervision?.viewingLogs?.find(
        (log) => log.supervisorId === supervisorId && log.isActive
      );

      if (activeLog) {
        console.warn(
          `Supervisor ${supervisorName} already has an active viewing session for this note`
        );
        return activeLog.id;
      }

      // Create new viewing log entry
      const newViewingLog: SupervisionViewingLog = {
        id: viewingLogId,
        supervisorId,
        supervisorName,
        startTime: Timestamp.now(),
        method,
        isActive: true,
        billingCode,
      };

      // Add to supervision data
      const currentSupervision = sessionNote.supervision || {
        billingCode: billingCode || '97155',
        supervisionStartTime: Timestamp.now(),
        supervisionEndTime: Timestamp.now(),
        supervisionDuration: 0,
        supervisionMethod: method,
        supervisionNotes: '',
        viewingLogs: [],
        totalViewingTime: 0,
      };

      const updatedViewingLogs = [
        ...(currentSupervision.viewingLogs || []),
        newViewingLog,
      ];

      await updateDoc(sessionNoteRef, {
        'supervision.viewingLogs': updatedViewingLogs,
        'timestamps.supervisorReviewStarted':
          currentSupervision.viewingLogs?.length === 0
            ? serverTimestamp()
            : sessionNote.timestamps.supervisorReviewStarted,
        updatedAt: serverTimestamp(),
      });

      console.log(
        `Started viewing session ${viewingLogId} for supervisor ${supervisorName}`
      );
      return viewingLogId;
    } catch (error) {
      console.error('Error starting viewing session:', error);
      throw error;
    }
  }

  /**
   * End a supervision viewing session
   * Called automatically when supervisor closes a session note or navigates away
   */
  async endViewingSession(
    sessionNoteId: string,
    viewingLogId: string,
    notes?: string
  ): Promise<void> {
    try {
      const sessionNoteRef = doc(db, 'sessionNotes', sessionNoteId);
      const sessionNoteDoc = await getDoc(sessionNoteRef);

      if (!sessionNoteDoc.exists()) {
        throw new Error('Session note not found');
      }

      const sessionNote = sessionNoteDoc.data() as SessionNote;

      if (!sessionNote.supervision?.viewingLogs) {
        throw new Error('No viewing logs found');
      }

      // Find the viewing log to end
      const viewingLogIndex = sessionNote.supervision.viewingLogs.findIndex(
        (log) => log.id === viewingLogId
      );

      if (viewingLogIndex === -1) {
        throw new Error('Viewing log not found');
      }

      const viewingLog = sessionNote.supervision.viewingLogs[viewingLogIndex];

      if (!viewingLog.isActive) {
        console.warn('Viewing log is already ended');
        return;
      }

      // Calculate duration in minutes
      const endTime = Timestamp.now();
      const durationMs =
        endTime.toMillis() - viewingLog.startTime.toMillis();
      const durationMinutes = Math.round(durationMs / 60000); // Convert to minutes

      // Update the viewing log
      const updatedViewingLog: SupervisionViewingLog = {
        ...viewingLog,
        endTime,
        duration: durationMinutes,
        isActive: false,
        notes,
      };

      // Replace the viewing log in the array
      const updatedViewingLogs = [...sessionNote.supervision.viewingLogs];
      updatedViewingLogs[viewingLogIndex] = updatedViewingLog;

      // Calculate total viewing time
      const totalViewingTime = updatedViewingLogs
        .filter((log) => !log.isActive && log.duration)
        .reduce((sum, log) => sum + (log.duration || 0), 0);

      await updateDoc(sessionNoteRef, {
        'supervision.viewingLogs': updatedViewingLogs,
        'supervision.totalViewingTime': totalViewingTime,
        updatedAt: serverTimestamp(),
      });

      console.log(
        `Ended viewing session ${viewingLogId}. Duration: ${durationMinutes} minutes`
      );
    } catch (error) {
      console.error('Error ending viewing session:', error);
      throw error;
    }
  }

  /**
   * Get active viewing session for a supervisor on a specific session note
   */
  async getActiveViewingSession(
    sessionNoteId: string,
    supervisorId: string
  ): Promise<SupervisionViewingLog | null> {
    try {
      const sessionNoteRef = doc(db, 'sessionNotes', sessionNoteId);
      const sessionNoteDoc = await getDoc(sessionNoteRef);

      if (!sessionNoteDoc.exists()) {
        return null;
      }

      const sessionNote = sessionNoteDoc.data() as SessionNote;

      const activeLog = sessionNote.supervision?.viewingLogs?.find(
        (log) => log.supervisorId === supervisorId && log.isActive
      );

      return activeLog || null;
    } catch (error) {
      console.error('Error getting active viewing session:', error);
      return null;
    }
  }

  /**
   * End all active viewing sessions for a supervisor
   * Useful for cleanup when supervisor logs out or closes browser
   */
  async endAllActiveSessionsForSupervisor(
    supervisorId: string
  ): Promise<void> {
    try {
      // Query all session notes with active viewing sessions for this supervisor
      // Note: This would require a Firestore query with array-contains
      // For now, this is a placeholder for the concept
      console.log(
        `Ending all active sessions for supervisor ${supervisorId}`
      );
      // Implementation would require maintaining a separate collection
      // of active viewing sessions or using Firestore queries
    } catch (error) {
      console.error('Error ending all active sessions:', error);
      throw error;
    }
  }

  /**
   * Get viewing statistics for a session note
   */
  async getViewingStatistics(sessionNoteId: string): Promise<{
    totalViewingTime: number;
    uniqueSupervisors: number;
    viewingSessions: number;
    averageSessionDuration: number;
  }> {
    try {
      const sessionNoteRef = doc(db, 'sessionNotes', sessionNoteId);
      const sessionNoteDoc = await getDoc(sessionNoteRef);

      if (!sessionNoteDoc.exists()) {
        throw new Error('Session note not found');
      }

      const sessionNote = sessionNoteDoc.data() as SessionNote;
      const viewingLogs = sessionNote.supervision?.viewingLogs || [];

      const completedLogs = viewingLogs.filter(
        (log) => !log.isActive && log.duration
      );

      const totalViewingTime = completedLogs.reduce(
        (sum, log) => sum + (log.duration || 0),
        0
      );

      const uniqueSupervisors = new Set(
        viewingLogs.map((log) => log.supervisorId)
      ).size;

      const viewingSessions = viewingLogs.length;

      const averageSessionDuration =
        completedLogs.length > 0
          ? Math.round(totalViewingTime / completedLogs.length)
          : 0;

      return {
        totalViewingTime,
        uniqueSupervisors,
        viewingSessions,
        averageSessionDuration,
      };
    } catch (error) {
      console.error('Error getting viewing statistics:', error);
      throw error;
    }
  }

  /**
   * Format viewing time for display
   */
  formatViewingTime(minutes: number): string {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;

    if (hours === 0) {
      return `${mins} min`;
    }

    return `${hours}h ${mins}m`;
  }

  /**
   * Check if minimum supervision time requirement is met
   * ABA therapy typically requires certain supervision percentages
   */
  isSupervisionRequirementMet(
    supervisionMinutes: number,
    sessionMinutes: number,
    requiredPercentage: number = 10 // Default 10% supervision requirement
  ): boolean {
    if (sessionMinutes === 0) return false;

    const actualPercentage = (supervisionMinutes / sessionMinutes) * 100;
    return actualPercentage >= requiredPercentage;
  }
}

export const supervisionTrackingService = new SupervisionTrackingService();
